import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const typeToMeta = {
  gitsawe: { name: 'ግጻዌ', path: '/gitsawie' },
  sinksar: { name: 'ስንክሳር', path: '/sinksar' },
  mahlet: { name: 'ማኅሌት', path: '/mahlet' },
  wudasie: { name: 'ውዳሴ ማርያም', path: '/wudasie' },
  seatat: { name: 'መጽሐፈ ሰዓታት', path: '/seatat' },
  hibuat: { name: 'ኅቡዓት', path: '/hibuat' },
  filseta: { name: 'እትአመነኪ', path: '/filseta' },
};

const GlobalSearch = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const resultsRef = useRef(null);
  const apiURL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleResultClick = useCallback((result) => {
    onClose();
    navigate(result.path, { 
      state: { 
        ...result.params, 
        searchTerm,
        highlightTerm: searchTerm 
      } 
    });
  }, [navigate, onClose, searchTerm]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && searchResults[selectedIndex]) {
          handleResultClick(searchResults[selectedIndex]);
        }
        break;
      default:
        break;
    }
  }, [isOpen, searchResults, selectedIndex, onClose, handleResultClick]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Debounced search directly in MongoDB
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${apiURL}/content?search=${encodeURIComponent(searchTerm.trim())}`);
        if (res.ok) {
          const json = await res.json();
          if (json.success && Array.isArray(json.data)) {
            const mapped = json.data.map(item => {
              const meta = typeToMeta[item.contentType] || { name: item.contentType, path: `/${item.contentType}` };
              let snippet = 'በርዕስ ውስጥ ተገኝቷል';
              if (Array.isArray(item.content)) {
                const firstWithMain = item.content.find(c => c && c.main);
                if (firstWithMain?.main) snippet = firstWithMain.main.substring(0, 120) + '...';
              } else if (typeof item.content === 'string') {
                snippet = item.content.substring(0, 120) + '...';
              }

              return {
                type: item.contentType,
                title: item.title,
                subtitle: `${meta.name}${item.month ? ` - ${item.month}` : ''}`,
                snippet,
                path: meta.path,
                params: {
                  month: item.month,
                  day: item.day,
                  id: item.itemId || item._id
                }
              };
            });
            setSearchResults(mapped);
          }
        }
      } catch (err) {
        console.error('Global search error:', err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, apiURL]);

  const highlightText = (text) => {
    if (!searchTerm.trim() || !text) return text;
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <mark key={i} className="bg-yellow-200 px-1 rounded">{part}</mark>
      ) : (
        part
      )
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Search Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              🔍 ሁለንተናዊ ፍለጋ (MongoDB)
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ✕
            </button>
          </div>
          
          <input
            ref={searchInputRef}
            type="text"
            placeholder="በሁሉም ይዘቶች ውስጥ ፈልግ... (ለምሳሌ: ቅዱስ ገብርኤል፣ ድንግል ማርያም፣ ወንጌል)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 rounded-lg text-gray-800 text-lg focus:outline-none focus:ring-2 focus:ring-white"
          />
        </div>

        {/* Search Results */}
        <div className="max-h-[60vh] overflow-y-auto" ref={resultsRef}>
          {loading && (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">በመፈለግ ላይ...</p>
            </div>
          )}

          {!loading && searchTerm && searchResults.length === 0 && (
            <div className="p-8 text-center">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">ምንም ውጤት አልተገኘም</h3>
              <p className="text-gray-600">
                ለ "<span className="font-medium">{searchTerm}</span>" ምንም ውጤት አልተገኘም
              </p>
            </div>
          )}

          {!loading && searchResults.length > 0 && (
            <div className="p-4">
              <div className="text-sm text-gray-600 mb-4">
                {searchResults.length} ውጤቶች ተገኝተዋል
              </div>
              
              <div className="space-y-2">
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                      selectedIndex === index
                        ? 'bg-blue-50 border-blue-300 shadow-md'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                    onClick={() => handleResultClick(result)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-800 text-lg">
                        {highlightText(result.title)}
                      </h3>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {result.subtitle}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {highlightText(result.snippet)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && !searchTerm && (
            <div className="p-8 text-center">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">ሁለንተናዊ ፍለጋ</h3>
              <p className="text-gray-600 mb-4">
                በሁሉም ይዘቶች ውስጥ ፈልግ - ግጻዌ፣ ስንክሳር፣ ውዳሴ ማርያም፣ መጽሐፈ ሰአታት እና ሌሎች
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
